// utils/intents/intentPreprocessor.js

import { detectIntent, isTriviaQuestion, isTriviaAnswer } from './intentUtils'; 

/**
 * 🔍 Preprocess intent to resolve fallbacks and enrich with known relationships
 * @param {Object} intentInfo - Output from intent detection
 * @param {string} message - Original user message
 * @param {string|null} lastTarget - Last remembered target (e.g., "mother")
 * @param {Array} relationships - List of known relationships for user
 * @returns {Object} Updated intentInfo
 */
export function preprocessIntent(intentInfo, message, lastTarget, relationships = []) {
  if (!intentInfo) return intentInfo;

  const lower = message.toLowerCase();
  const memoryIntents = ['memory_recall', 'recall_request'];
  const lossIntents = ['loss'];

  // 1️⃣ Fallback to last known target (ONLY for memory-related intents)
  if (
    memoryIntents.includes(intentInfo.intent) &&
    !intentInfo.target
  ) {
    console.log(`🔁 Using last known target: ${lastTarget}`);
    intentInfo.target = lastTarget;
  }

  // 2️⃣ Override target if a matching relationship exists (ONLY for memory or loss intents)
  if (
    (memoryIntents.includes(intentInfo.intent) || lossIntents.includes(intentInfo.intent)) &&
    relationships.length
  ) {
    const override = relationships.find(rel =>
      rel.relationship === intentInfo.target ||
      (rel.first_name && lower.includes(rel.first_name.toLowerCase()))
    );

    if (override) {
      console.log(`🛠 Overriding target "${intentInfo.target}" → "${override.relationship}"`);
      intentInfo.target = override.relationship;
    }
  }

  return intentInfo;
}


/**
 * 🧠 Get the first name associated with a relationship match
 * Useful for more human-style memory injection ("my mom Marilyn")
 * @param {Object} intentInfo - Processed intent object
 * @param {Array} relationships - Known relationship records
 * @returns {string|null} First name if match found, else null
 */
export function getMemoryName(intentInfo, relationships = []) {
  if (
    ['memory_recall', 'recall_request', 'loss'].includes(intentInfo.intent) &&
    intentInfo.target &&
    relationships.length
  ) {
    const match = relationships.find(
      rel => rel.relationship === intentInfo.target && rel.first_name
    );
    if (match) {
      console.log(`📛 Memory name matched for "${intentInfo.target}": ${match.first_name}`);
      return match.first_name;
    }
  }

  return null;
}

/**
 * 🧠 Full Intent Resolution Pipeline
 * Runs detection, preprocessing, and memory name resolution in one call
 * @param {string} message - Incoming user message
 * @param {string|null} lastTarget - Last known target reference
 * @param {Array} relationships - Relationship records for user
 * @returns {Object} Enriched intent object with memoryName if found
 */


export async function getIntentInfo(message, lastTarget, relationships = []) {
  const lower = message.toLowerCase();

  // 📚 Trivia detection shortcut
  if (isTriviaQuestion(lower)) {
    console.log(`[📚 getIntentInfo] Detected trivia question`);
    return { intent: 'trivia_question', target: null, memoryName: null };
  }

  if (isTriviaAnswer(lower)) {
    console.log(`[📚 getIntentInfo] Detected trivia answer`);
    return { intent: 'trivia_answer', target: null, memoryName: null };
  }

  // 🧠 Fallback to regular intent detection
  const intentInfo = await detectIntent(message);
  console.log(`🎯 Detected intent: ${JSON.stringify(intentInfo)}`);

  if (intentInfo.target) {
    lastTarget = intentInfo.target;
  }

  const preprocessedIntent = preprocessIntent(intentInfo, message, lastTarget, relationships);
  const memoryName = getMemoryName(preprocessedIntent, relationships);

  return { ...preprocessedIntent, memoryName };
}

